/**
 * Persistent user-facing theme + accent color.
 * - mode: "light" | "dark" | "auto"  (auto follows OS preference)
 * - accent: "orange" | "blue" | "green" | "purple" | "pink" | "red"
 * Stored in localStorage, applied to <html> via class + data-accent attribute.
 */
import { useEffect, useState, useCallback } from "react";

const MODE_KEY = "theme_mode";
const ACCENT_KEY = "theme_accent";

export const ACCENT_OPTIONS = [
  { key: "orange", label: "برتقالي (الافتراضي)", swatch: "#f97316" },
  { key: "blue", label: "أزرق", swatch: "#3b82f6" },
  { key: "green", label: "أخضر", swatch: "#10b981" },
  { key: "purple", label: "بنفسجي", swatch: "#a855f7" },
  { key: "pink", label: "وردي", swatch: "#ec4899" },
  { key: "red", label: "أحمر", swatch: "#ef4444" },
];

export const MODE_OPTIONS = [
  { key: "light", label: "فاتح" },
  { key: "dark", label: "داكن" },
  { key: "auto", label: "تلقائي حسب النظام" },
];

function resolveMode(mode) {
  if (mode !== "auto") return mode;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch { return "light"; }
}

function apply(mode, accent) {
  const root = document.documentElement;
  const effective = resolveMode(mode);
  root.classList.toggle("dark", effective === "dark");
  root.setAttribute("data-accent", accent);
}

/** Apply saved theme immediately on app boot — call once at index.js */
export function bootstrapTheme() {
  const mode = localStorage.getItem(MODE_KEY) || "light";
  const accent = localStorage.getItem(ACCENT_KEY) || "orange";
  apply(mode, accent);
}

export function useTheme() {
  const [mode, setMode] = useState(() => localStorage.getItem(MODE_KEY) || "light");
  const [accent, setAccent] = useState(() => localStorage.getItem(ACCENT_KEY) || "orange");

  useEffect(() => { apply(mode, accent); }, [mode, accent]);

  // Follow OS preference changes when in auto mode
  useEffect(() => {
    if (mode !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => apply("auto", accent);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [mode, accent]);

  const updateMode = useCallback((m) => {
    setMode(m); localStorage.setItem(MODE_KEY, m);
  }, []);
  const updateAccent = useCallback((a) => {
    setAccent(a); localStorage.setItem(ACCENT_KEY, a);
  }, []);

  return { mode, accent, setMode: updateMode, setAccent: updateAccent };
}
